import React from 'react';

// Shared figure primitives for the cognition task renderers.
//
// Every figure in this file draws into the same square VIEW x VIEW user space
// and is sized in CSS pixels by a `size` prop, so two figures placed side by
// side are always at the same scale. Anything added here must keep that box —
// two figures drawn against different view sizes silently disagree about scale,
// which is the shared-transform bug in another costume.
//
// mentalRotation.jsx deliberately keeps its own module-private PolylineFigure
// alongside its own VIEW and PADDING. Do NOT fold it in here: its tests pin the
// rendered span those constants produce and assert one element per <svg>.
const VIEW = 100;

// The bounding box of a set of [row, col] cells, in cells, on the LONGER axis.
//
// Exported because it is the only way a caller can hold the CELL — rather than
// the box — constant across figures: a box of `cell * polyominoSpan(cells)`
// draws every square at exactly `cell` pixels whatever shape it is drawing, so
// two figures sized that way are made of identical squares. Sizing every figure
// to one box does the opposite, and scales a two-cell shape up to the size of a
// six-cell one.
export function polyominoSpan(cells) {
  const list = Array.isArray(cells) ? cells : [];
  if (list.length === 0) return 0;
  return Math.max(...list.map(([r]) => r), ...list.map(([, c]) => c)) + 1;
}

// A set of [row, col] cells drawn on its own bounding box. Every shape the
// server sends is already normalised so its minimum row and column are 0, so
// the bounding box is exactly (maxRow+1) x (maxCol+1).
//
// Cell size is derived from the LONGER side and both axes use it, so a shape is
// never stretched. A stretched option would be distinguishable from the others
// by aspect ratio alone, which is a cue that has nothing to do with assembly.
//
// Cells are drawn in the order given. Nothing is sorted, deduped, rotated,
// reflected or re-normalised here: the caller's array IS the shape, at the
// orientation the caller was handed it.
export function PolyominoGrid({ cells, size = 72, label, filled = 'var(--text-primary)' }) {
  const list = Array.isArray(cells) ? cells : [];
  if (list.length === 0) return null;

  const maxRow = Math.max(...list.map(([r]) => r));
  const maxCol = Math.max(...list.map(([, c]) => c));
  const unit = VIEW / polyominoSpan(list);
  // Centred on the shorter axis, so a wide shape and a tall one sit in the same
  // place in the box rather than both hugging the top-left corner.
  const offsetX = (VIEW - (maxCol + 1) * unit) / 2;
  const offsetY = (VIEW - (maxRow + 1) * unit) / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="img"
      aria-label={label}
      focusable="false"
    >
      {list.map(([r, c], i) => (
        <rect
          key={i}
          x={offsetX + c * unit}
          y={offsetY + r * unit}
          width={unit}
          height={unit}
          fill={filled}
          stroke="var(--bg-primary)"
          strokeWidth={unit * 0.06}
        />
      ))}
    </svg>
  );
}

// Six values per attribute on an ORDERED scale, because the rules are
// progressions and a progression is only perceptible on an ordered scale. The
// key IS the position on the scale: 3 sides through 8 sides.
const SIDES = { triangle: 3, square: 4, pentagon: 5, hexagon: 6, heptagon: 7, octagon: 8 };

// A monotone lightness ramp on one hue, NOT six arbitrary colours. The rule that
// governs shade is a progression, so shade must read as ordered — six distinct
// hues would be a nominal scale and the progression would be invisible.
const SHADE_FILL = {
  white: 0.0, pale: 0.2, light: 0.4, medium: 0.6, dark: 0.8, black: 1.0,
};

// The empty slot is inset inside the same VIEW box every other figure draws
// into, so the hole sits exactly where a drawn cell would and is the same size.
const SLOT_INSET = 4;
const SLOT_SPAN = VIEW - SLOT_INSET * 2;
const SLOT_GLYPH_SIZE = 34;

function polygonPoints(sides, cx, cy, r) {
  // Start at -90 degrees so every polygon sits point-up and side count is read
  // from the outline rather than from an accidental orientation.
  return Array.from({ length: sides }, (_, i) => {
    const a = (Math.PI * 2 * i) / sides - Math.PI / 2;
    return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
  }).join(' ');
}

// One cell of a matrix, or one option. All three attributes must be
// simultaneously legible: count is drawn as n copies (never a numeral, which
// would let the participant compare text instead of the figure), shape is read
// from the side count, and shade from an ordered ramp.
//
// Nothing here knows which figure completes a pattern, and nothing may: the
// rule is induced by the participant and the answer is graded by the server.
// A figure is drawn from its own three attributes and from nothing else.
export function MatrixFigure({ figure, size = 72, label }) {
  if (!figure) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        role="img"
        aria-label={label || 'empty slot'}
        focusable="false"
      >
        <rect
          x={SLOT_INSET}
          y={SLOT_INSET}
          width={SLOT_SPAN}
          height={SLOT_SPAN}
          rx="8"
          fill="none"
          stroke="var(--border-strong)"
          strokeWidth="3"
          strokeDasharray="8 6"
        />
        {/* Nudged below the centre line so the glyph reads as vertically centred. */}
        <text
          x={VIEW / 2}
          y={VIEW / 2 + 6}
          textAnchor="middle"
          fontSize={SLOT_GLYPH_SIZE}
          fill="var(--text-tertiary)"
        >
          ?
        </text>
      </svg>
    );
  }
  const sides = SIDES[figure.shape] || 3;
  const fill = SHADE_FILL[figure.shade] ?? 0;
  const n = Number(figure.count) || 1;
  const cols = n <= 2 ? n : n <= 4 ? 2 : 3;
  const rows = Math.ceil(n / cols);
  const cw = VIEW / cols;
  const ch = VIEW / rows;
  const r = Math.min(cw, ch) * 0.38;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="img"
      aria-label={label || `${n} ${figure.shade} ${figure.shape}${n > 1 ? 's' : ''}`}
      focusable="false"
    >
      {Array.from({ length: n }, (_, i) => (
        <polygon
          key={i}
          points={polygonPoints(sides, (i % cols) * cw + cw / 2, Math.floor(i / cols) * ch + ch / 2, r)}
          fill="var(--text-primary)"
          fillOpacity={fill}
          stroke="var(--text-primary)"
          strokeWidth="2.4"
        />
      ))}
    </svg>
  );
}
